import { Format } from "./formatHelper";

type NavigatorLike = Navigator & { userLanguage?: string };

type ScheduleEntry = {
  day: number;
  time: number;
};

type NextUserHour = {
  day: number;
  time: number;
  userTime: string;
};

export function formatDateWithLocale(i18nLocale: string): string {

  const formatMapping = {
    day: 'd',
    month: 'm',
    year: 'Y',
    dayPeriod: '',
    era: '',
    hour: '',
    minute: '',
    second: '',
    timeZoneName: '',
    weekday: '',
    unknown: '',
    fractionalSecond: ''
  };

  const dateOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  };

  const dateFormatter = new Intl.DateTimeFormat(i18nLocale, dateOptions);

  const dateFormat = dateFormatter.formatToParts(new Date())
    .map(part => part.type === 'literal' ? part.value : formatMapping[part.type])
    .join('');

  return dateFormat;
}


/// Date to string
export function formatDate(date: Date, format?: string) {
  if (date == undefined || date == null) {
    return "";
  }

  if (!format || format == '') {
    format = "yyyy-MM-ddTHH:mm";
  }

  let result = format;

  if (format.indexOf("yyyy") != -1) {
    result = result.replace("yyyy", date.getFullYear().toString());
  }
  else if (format.indexOf("yy") != -1) {
    result = result.replace("yy", date.getFullYear().toString());
  }

  const month = pad0(date.getMonth() + 1);
  if (format.indexOf("MM") != -1) {
    result = result.replace("MM", month);
  }
  else if (format.indexOf("M") != -1) {
    result = result.replace("M", month);
  }

  const day = date.getDate();
  const strDay = pad0(day);

  if (format.indexOf("dd") != -1) {
    result = result.replace("dd", strDay);
  }
  else if (format.indexOf("d") != -1) {
    result = result.replace("d", strDay);
  }

  let hour = pad0(date.getHours());
  if (format.indexOf("HH") != -1) {
    result = result.replace("HH", hour);
  } else if (format.indexOf("H") != -1) {
    result = result.replace("H", hour);
  } else if (format.indexOf("h") != -1) {
    const dHours = date.getHours();
    if (dHours > 12) {
      hour = (dHours - 12).toString();
      result = result.replace("tt", "pm");
    } else {
      result = result.replace("tt", "am");
    }
    result = result.replace("h", hour);
  }
  if (format.indexOf("mm") != -1) {
    const min = pad0(date.getMinutes());
    result = result.replace("mm", min);
  }

  return result;
}

function pad0(input: number) {
  let inputStr = input.toString();
  if (input < 10) {
    inputStr = 0 + inputStr;
  }
  return inputStr;
}

export function toSystemString(value: Date | null): string | null {
  if (!value || Number.isNaN(value.getTime())) {
    return null;
  }
  const year = value.getFullYear();
  const month = pad0(value.getMonth() + 1);
  const day = pad0(value.getDate());
  const hours = pad0(value.getHours());
  const minutes = pad0(value.getMinutes());
  const seconds = pad0(value.getSeconds());
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

export function formatLocalDateTime(value: Date | null): string | null {
  return toSystemString(value);
}

export class DateHelper {
  static toSystemString(value: Date | null): string | null {
    return toSystemString(value);
  }
}

export function setTimeToDate(date: Date, hour: string) {
  const [timePart, ampmPart] = hour.split(' ');
  let hours = timePart.split(/[:.]/).map(Number)[0];
  const minutes = timePart.split(/[:.]/).map(Number)[1];

  if (isNaN(hours) || isNaN(minutes)) {
    return date;
  }

  if (ampmPart) {
    const isPM = ampmPart.toLowerCase().includes('p');
    if (isPM && hours !== 12) {
      hours += 12;
    } else if (!isPM && hours === 12) {
      hours = 0;
    }
  }
  date.setHours(hours);
  date.setMinutes(minutes);

  return date;
}

export function formatTime(date: Date, nav: NavigatorLike) {
  const userLanguage = nav.language || nav.userLanguage || "en-US";
  const timeFormatter = new Intl.DateTimeFormat(userLanguage, {
    hour: "numeric",
    minute: "numeric",
  });

  return timeFormatter.format(date);
}

export function beautifyDate(date: string, showWeekDay?: boolean, showYear?: boolean) {
  if (Format.IsNull(date)) {
    return "";
  }
  const options: Intl.DateTimeFormatOptions = {
    //weekday: "long",
    // year: "numeric",
    month: "long",
    day: "numeric",
    hour: 'numeric',
    minute: 'numeric'
  };
  if (showWeekDay === true || showWeekDay === undefined) {
    options.weekday = "long";
  }
  if (showYear === true || showYear === undefined) {
    options.year = "numeric";
  }
  const str = capitalizeFirstLetter(new Date(date).toLocaleString(navigator.language, options));
  return str;
}

export function beautifyDateWithOutTime(date: string, showWeekDay?: boolean, showYear?: boolean) {
  if (Format.IsNull(date)) {
    return "";
  }
  const options: Intl.DateTimeFormatOptions = {
    month: "long",
    day: "numeric",
  };
  if (showWeekDay === true || showWeekDay === undefined) {
    options.weekday = "long";
  }
  if (showYear === true || showYear === undefined) {
    options.year = "numeric";
  }
  const str = capitalizeFirstLetter(new Date(date).toLocaleString(navigator.language, options));
  return str;
}

export function capitalizeFirstLetter(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}


export function beautifyShortDate(date: string) {
  const options: Intl.DateTimeFormatOptions = {
    month: "long",
    day: "numeric",
    hour: 'numeric',
    minute: 'numeric'
  };
  return new Date(date).toLocaleString(navigator.language, options);
}


export function advance30min(date: Date): Date {
  // Create a new date object based on the current date
  const advancedDate = new Date(date);

  // Approximate to the next 30 minutes
  const minutes = advancedDate.getMinutes();
  const next30Minutes = Math.ceil(minutes / 30) * 30;

  if (next30Minutes === 0) {
    advancedDate.setMinutes(30);
    advancedDate.setHours(advancedDate.getHours() + 1);
  } else {
    advancedDate.setMinutes(next30Minutes);
  }

  return advancedDate;
}

export function getDaysBetweenDates(date1: Date, date2: Date) {
  // Convert the dates to milliseconds
  const date1Ms = date1.getTime();
  const date2Ms = date2.getTime();

  // Calculate the difference in milliseconds
  const differenceMs = Math.abs(date2Ms - date1Ms);

  // Convert the difference to days
  const days = Math.floor(differenceMs / (1000 * 60 * 60 * 24));

  return days;
}

export function getNextUserHourAndDay(schedule: ScheduleEntry[] | null | undefined): NextUserHour | null {
  if (schedule == undefined || schedule == null) {
    return null;
  }
  const currentDate = new Date();
  const currentDay = currentDate.getDay(); // Sunday: 0, Monday: 1, ..., Saturday: 6
  const currentHour = currentDate.getHours();

  let nextHour: NextUserHour = { day: currentDay, time: currentHour, userTime: '' };//this is today default, if days and hours are before today, the get today as next day

  for (let i = 0; i < schedule.length; i++) {
    const { day, time } = schedule[i];

    if (day > currentDay || (day === currentDay && time > currentHour)) {
      nextHour = { day, time, userTime: '' };
      break;
    }

  }

  const hours = String(nextHour.time).padStart(2, '0');
  const minutes = '00';
  const userTime = `${hours}:${minutes}`;
  nextHour.userTime = userTime;

  return nextHour;
}

export function toStartOfDayIso(date: Date | string | number): string {
  const candidate = date instanceof Date
    ? new Date(date.getTime())
    : (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)
      ? new Date(`${date}T00:00:00`)
      : new Date(date));
  const base = Number.isNaN(candidate.getTime()) ? new Date() : candidate;
  const year = base.getFullYear();
  const month = pad0(base.getMonth() + 1);
  const day = pad0(base.getDate());
  return `${year}-${month}-${day}T00:00:00`;
}

export function extractDatePart(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }
  const raw = typeof value === 'string' ? value : String(value);
  if (!raw) {
    return null;
  }
  return raw.includes('T') ? raw.split('T')[0] : raw;
}

// Function to extract time from the date string
export function getTimeFromDateString(dateStr: string) {
  if (dateStr == null || dateStr == undefined || dateStr == "") {
    return null;
  }
  const date = new Date(dateStr);
  const hours = date.getHours();
  const minutes = date.getMinutes();

  // Format the time as "hh:mm"
  return `${formatNumber(hours)}:${formatNumber(minutes)}`;
}

// Helper function to format numbers as two digits
function formatNumber(number: number) {
  return number.toString().padStart(2, "0");
}

export function addMinutes(date: Date, mins: number) {
  return dateAdd(date, 'minute', mins);
}


export function dateAdd(date: Date, interval: string, units: number) {
  if (date == null) return;

  const ret = new Date(date); //don't change original date
  const checkRollover = function () { if (ret.getDate() != date.getDate()) ret.setDate(0); };
  switch (String(interval).toLowerCase()) {
    case 'year': ret.setFullYear(ret.getFullYear() + units); checkRollover(); break;
    case 'quarter': ret.setMonth(ret.getMonth() + 3 * units); checkRollover(); break;
    case 'month': ret.setMonth(ret.getMonth() + units); checkRollover(); break;
    case 'week': ret.setDate(ret.getDate() + 7 * units); break;
    case 'day': ret.setDate(ret.getDate() + units); break;
    case 'hour': ret.setTime(ret.getTime() + units * 3600000); break;
    case 'minute': ret.setTime(ret.getTime() + units * 60000); break;
    case 'second': ret.setTime(ret.getTime() + units * 1000); break;
    default: return null;
  }
  return ret;
}
